import { useState, useEffect } from 'react';
import styles from '../../styles/workflows.module.css';
import api from '../../API.js';

function WorkflowForm({ workflow, onSubmit, onCancel, disabled = false }) {
    const [formData, setFormData] = useState({
        name: workflow?.name || '',
        description: workflow?.description || '',
        steps: workflow?.steps || [],
        metadataFeature: workflow?.metadata?.feature || '',
    });

    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (workflow) {
            setFormData({
                name: workflow.name || '',
                description: workflow.description || '',
                steps: workflow.steps || [],
                metadataFeature: workflow.metadata?.feature || '',
            });
        }
        loadAgents();
    }, [workflow]);

    const loadAgents = async () => {
        try {
            const response = await api.get('agent', { query: { activeOnly: true } });
            if (response.status === 200) {
                const agentList = response.data.data || response.data;
                setAgents(agentList || []);
            }
        } catch (err) {
            console.error('Error loading agents:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Validate required fields
            if (!formData.name.trim()) {
                throw new Error('Workflow name is required');
            }
            if (formData.steps.length === 0) {
                throw new Error('At least one step is required');
            }

            // Validate steps
            for (const step of formData.steps) {
                if (!step.stepId || !step.stepId.trim()) {
                    throw new Error('All steps must have a step ID');
                }
                if (!step.agentId || !step.agentId.trim()) {
                    throw new Error('All steps must have an agent selected');
                }
            }

            // Prepare FormData for submission
            const formDataToSubmit = new FormData();
            formDataToSubmit.append('name', formData.name.trim());
            formDataToSubmit.append('description', formData.description.trim());
            formDataToSubmit.append('steps', JSON.stringify(formData.steps));
            
            // Add metadata if feature key is provided
            if (formData.metadataFeature && formData.metadataFeature.trim()) {
                formDataToSubmit.append('metadata', JSON.stringify({
                    feature: formData.metadataFeature.trim()
                }));
            }

            await onSubmit(formDataToSubmit);
        } catch (err) {
            setError(err.message || 'Failed to save workflow');
        } finally {
            setLoading(false);
        }
    };

    const handleAddStep = () => {
        setFormData(prev => ({
            ...prev,
            steps: [...prev.steps, {
                stepId: `step_${prev.steps.length + 1}`,
                agentId: '',
                inputMapping: {},
                outputMapping: {},
                condition: '',
            }]
        }));
    };

    const handleRemoveStep = (index) => {
        setFormData(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index)
        }));
    };

    const handleUpdateStep = (index, field, value) => {
        setFormData(prev => {
            const updatedSteps = [...prev.steps];
            updatedSteps[index] = { ...updatedSteps[index], [field]: value };
            return { ...prev, steps: updatedSteps };
        });
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Basic Information</h3>
                
                <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                        Workflow Name
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        disabled={disabled}
                        className={styles.formInput}
                        placeholder="e.g., Draft and Expand Workflow"
                    />
                </div>

                {workflow && workflow.workflowId && (
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                            Workflow ID
                        </label>
                        <input
                            type="text"
                            value={workflow.workflowId}
                            disabled
                            readOnly
                            className={styles.formInput}
                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                        <div className={styles.formHelp} style={{ color: '#666', fontStyle: 'italic' }}>
                            Workflow ID cannot be changed once set.
                        </div>
                    </div>
                )}

                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        disabled={disabled}
                        className={`${styles.formInput} ${styles.formTextarea}`}
                        placeholder="Brief description of what this workflow does"
                        rows={3}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        Feature Key (Metadata)
                    </label>
                    <input
                        type="text"
                        value={formData.metadataFeature}
                        onChange={(e) => setFormData(prev => ({ ...prev, metadataFeature: e.target.value }))}
                        disabled={disabled}
                        className={styles.formInput}
                        placeholder="e.g., filename_matching"
                    />
                    <div className={styles.formHelp}>
                        This key maps this workflow to a UI feature. UIs can look up workflows by this key instead of hardcoding workflow IDs.
                    </div>
                </div>
            </div>

            <div className={styles.formSection}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
                    <h3 className={styles.formSectionTitle} style={{ margin: 0 }}>Workflow Steps</h3>
                    {!disabled && (
                        <button
                            type="button"
                            onClick={handleAddStep}
                            className={`${styles.button} ${styles.buttonSecondary}`}
                            style={{ padding: '0.5em 1em', fontSize: '14px' }}
                        >
                            Add Step
                        </button>
                    )}
                </div>

                {formData.steps.length === 0 ? (
                    <div className={styles.info}>
                        No steps defined. Click "Add Step" to add workflow steps.
                    </div>
                ) : (
                    formData.steps.map((step, index) => (
                        <div key={index} className={styles.stepCard}>
                            <div className={styles.stepHeader}>
                                <span className={styles.stepTitle}>Step {index + 1}</span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveStep(index)}
                                        className={styles.stepRemoveButton}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                            <div className={styles.formGroup}>
                                <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                                    Step ID
                                </label>
                                <input
                                    type="text"
                                    value={step.stepId || ''}
                                    onChange={(e) => handleUpdateStep(index, 'stepId', e.target.value)}
                                    disabled={disabled}
                                    className={styles.formInput}
                                    placeholder="e.g., step_1"
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                                    Agent
                                </label>
                                <select
                                    value={step.agentId || ''}
                                    onChange={(e) => handleUpdateStep(index, 'agentId', e.target.value)}
                                    disabled={disabled}
                                    className={styles.formInput}
                                    required
                                >
                                    <option value="">Select Agent</option>
                                    {agents.map(agent => (
                                        <option key={agent.agentId} value={agent.agentId}>
                                            {agent.name} ({agent.agentId})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div style={{ display: 'flex', gap: '1em', marginTop: '2em' }}>
                <button
                    type="submit"
                    disabled={loading || disabled}
                    className={`${styles.button} ${styles.buttonPrimary}`}
                >
                    {loading ? 'Saving...' : (workflow ? 'Update Workflow' : 'Create Workflow')}
                </button>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading || disabled}
                        className={`${styles.button} ${styles.buttonSecondary}`}
                    >
                        Cancel
                    </button>
                )}
            </div>
        </form>
    );
}

export default WorkflowForm;
